import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Mail, BellRing, Settings2, History, Send, ExternalLink,
  RefreshCw, FileText, Users, FlaskConical, MailCheck, MailWarning,
} from "lucide-react";
import RecipientsTab from "./comms/RecipientsTab";
import { SendNowDialog, TestSendDialog, fmtDateTime } from "./comms/SendDialogs";

const LEVEL_BADGE = {
  verde: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  amarelo: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  laranja: "bg-orange-500/15 text-orange-700 border-orange-500/30",
  vermelho: "bg-red-500/15 text-red-700 border-red-500/30",
};

const STATUS_BADGE = {
  aceito: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  entregue: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  falha: "bg-red-500/15 text-red-700 border-red-500/30",
  falha_retry: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  cancelado: "bg-muted text-muted-foreground",
  sem_destinatarios: "bg-amber-500/15 text-amber-700 border-amber-500/30",
};

const ORIGIN_BADGE = {
  automatico: "bg-sky-500/15 text-sky-700 border-sky-500/30",
  manual: "bg-violet-500/15 text-violet-700 border-violet-500/30",
  teste: "bg-amber-500/15 text-amber-700 border-amber-500/30",
};

const ORIGIN_OPTIONS = [
  { value: "", label: "Todas as origens" },
  { value: "automatico", label: "Automático" },
  { value: "manual", label: "Manual" },
  { value: "teste", label: "Teste" },
];


/**
 * Central de Comunicação Ambiental — Inteligência Ambiental 360.
 *
 * Pré-visualização dos DOIS modelos (boletim técnico e alerta imediato),
 * configuração de frequência/destinatários, envio de teste (simulação) e
 * histórico rastreável — tudo restrito às unidades autorizadas ao usuário.
 */
export default function EnvironmentCommunications() {
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("todos");
  const [unitId, setUnitId] = useState("todas");
  const [config, setConfig] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyOrigin, setHistoryOrigin] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewTab, setPreviewTab] = useState("boletim");
  const [previewKey, setPreviewKey] = useState(0);
  const [sendOpen, setSendOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);

  const selectedUnit = useMemo(
    () => units.find((u) => u.id === unitId) || null,
    [units, unitId],
  );

  const authParams = useMemo(
    () => ({
      client_id: selectedUnit?.client_id || undefined,
      unit_id: selectedUnit?.id || undefined,
    }),
    [selectedUnit],
  );

  // A aba Destinatários trabalha por cliente: usa a unidade selecionada
  // quando existe e, caso contrário, o primeiro cliente visível.
  const selectedClientId = useMemo(() => {
    if (selectedUnit?.client_id) return selectedUnit.client_id;
    if (clientId && clientId !== "todos") return clientId;
    return clients[0]?.id || null;
  }, [selectedUnit, clientId, clients]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/envintel/units");
      setUnits(data.units || []);
      setClients(data.clients || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedUnit) {
      setConfig(null);
      return;
    }
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/envintel/config", { params: authParams });
        if (active) setConfig(data.effective || null);
      } catch (err) {
        if (active) {
          setConfig(null);
          toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [selectedUnit, authParams]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/envintel/history", {
        params: {
          ...authParams,
          limit: 50,
          origin: historyOrigin || undefined,
        },
      });
      setHistory(data.records || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [authParams, historyOrigin]);

  const loadDashboard = useCallback(async () => {
    try {
      const { data } = await api.get("/envintel/dashboard", { params: authParams });
      setDashboard(data);
    } catch (err) {
      setDashboard(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [authParams]);

  useEffect(() => {
    loadDashboard();
    loadHistory();
  }, [loadDashboard, loadHistory]);

  const saveConfig = async (patch) => {
    if (!selectedUnit) return;
    setBusy(true);
    try {
      const { data } = await api.put("/envintel/config", {
        client_id: selectedUnit.client_id,
        unit_id: selectedUnit.id,
        config: patch,
      });
      setConfig(data.config || null);
      toast.success("Configuração salva e auditada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const refreshPreview = () => setPreviewKey((k) => k + 1);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <RefreshCw className="animate-spin mr-2" size={16} /> Carregando…
      </div>
    );
  }

  if (!units.length) {
    return (
      <Card className="p-8 text-center text-muted-foreground">
        Nenhuma unidade autorizada disponível para o seu perfil.
      </Card>
    );
  }

  const previewSrc = selectedUnit
    ? `/api/envintel/preview/${previewTab === "boletim" ? "bulletin" : "alert"}?${new URLSearchParams(authParams)}`
    : null;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">
            Central de Comunicação Ambiental
          </h1>
          <p className="text-sm text-muted-foreground">
            Boletins programados, alertas por alteração de risco e auditoria —
            isolados por cliente e unidade.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={clientId}
            onValueChange={(v) => {
              setClientId(v);
              setUnitId("todas");
            }}
          >
            <SelectTrigger className="w-52" data-testid="comms-client-select">
              <SelectValue placeholder="Cliente" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos (meu escopo)</SelectItem>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={unitId}
            onValueChange={(v) => setUnitId(v)}
            disabled={!units.length}
          >
            <SelectTrigger className="w-56" data-testid="comms-unit-select">
              <SelectValue placeholder="Unidade" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Selecione a unidade</SelectItem>
              {units
                .filter((u) => clientId === "todos" || u.client_id === clientId)
                .map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw size={14} className="mr-1" /> Atualizar
          </Button>
        </div>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <MetricCard
          label="Unidades monitoradas"
          value={dashboard?.units_monitored ?? 0}
          hint="no seu escopo"
        />
        <MetricCard
          label="Destinatários ativos"
          value={dashboard?.recipients?.ativos ?? 0}
          hint={`${dashboard?.recipients?.total ?? 0} cadastrado(s)`}
        />
        <MetricCard
          label="Comunicações enviadas"
          value={dashboard?.communications?.sent ?? 0}
          hint={`${dashboard?.communications?.pending ?? 0} pendente(s)`}
        />
        <MetricCard
          label="Falhas de processamento"
          value={dashboard?.communications?.failed ?? 0}
          tone={dashboard?.communications?.failed > 0 ? "danger" : undefined}
        />
        <MetricCard
          label="Boletins programados"
          value={dashboard?.scheduled_reports ?? 0}
          hint="com envio habilitado"
        />
      </div>

      <Tabs defaultValue="preview">
        <TabsList>
          <TabsTrigger value="preview" data-testid="tab-preview">
            <Mail size={14} className="mr-1" /> Pré-visualização
          </TabsTrigger>
          <TabsTrigger value="recipients" data-testid="tab-recipients">
            <Users size={14} className="mr-1" /> Destinatários
          </TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config">
            <Settings2 size={14} className="mr-1" /> Configurações
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History size={14} className="mr-1" /> Histórico
          </TabsTrigger>
        </TabsList>

        <TabsContent value="preview" className="space-y-3">
          {!selectedUnit ? (
            <Card className="p-6 text-sm text-muted-foreground">
              Selecione uma unidade para visualizar os modelos.
            </Card>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant={previewTab === "boletim" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPreviewTab("boletim")}
                  data-testid="btn-preview-bulletin"
                >
                  <FileText size={14} className="mr-1" /> Boletim técnico
                </Button>
                <Button
                  variant={previewTab === "alerta" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPreviewTab("alerta")}
                  data-testid="btn-preview-alert"
                >
                  <BellRing size={14} className="mr-1" /> Alerta imediato
                </Button>
                <div className="flex-1" />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={refreshPreview}
                  data-testid="btn-refresh-preview"
                >
                  <RefreshCw size={14} className="mr-1" /> Recarregar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setTestOpen(true)}
                  disabled={busy}
                  data-testid="btn-send-test"
                >
                  <FlaskConical size={14} className="mr-1" /> Enviar teste
                </Button>
                <Button
                  size="sm"
                  onClick={() => setSendOpen(true)}
                  disabled={busy}
                  data-testid="btn-send-now"
                >
                  <Send size={14} className="mr-1" /> Enviar agora
                </Button>
                <Button size="sm" variant="ghost" asChild>
                  <a href={previewSrc} target="_blank" rel="noreferrer">
                    <ExternalLink size={14} className="mr-1" />
                    Abrir pré-visualização
                  </a>
                </Button>
              </div>
              <Card className="overflow-hidden">
                <iframe
                  key={`${previewSrc}-${previewKey}`}
                  title="Pré-visualização da comunicação ambiental"
                  src={previewSrc}
                  className="w-full h-[70vh] bg-white"
                  data-testid="comms-preview-frame"
                />
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="recipients">
          <RecipientsTab
            clientId={selectedClientId}
            onChanged={loadDashboard}
          />
        </TabsContent>

        <TabsContent value="config" className="space-y-3">
          {!selectedUnit ? (
            <Card className="p-6 text-sm text-muted-foreground">
              Selecione uma unidade para editar a configuração.
            </Card>
          ) : (
            <Card className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Boletins automáticos</div>
                  <div className="text-xs text-muted-foreground">
                    Envio técnico programado para a unidade.
                  </div>
                </div>
                <Switch
                  checked={Boolean(config?.bulletins_enabled)}
                  disabled={busy}
                  onCheckedChange={(v) => saveConfig({ bulletins_enabled: v })}
                  data-testid="switch-bulletins"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">
                    Alertas por alteração de risco
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Notificação imediata ao trocar de faixa.
                  </div>
                </div>
                <Switch
                  checked={Boolean(config?.risk_alerts_enabled)}
                  disabled={busy}
                  onCheckedChange={(v) => saveConfig({ risk_alerts_enabled: v })}
                  data-testid="switch-alerts"
                />
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-sm space-y-1">
                  <span className="text-muted-foreground">
                    Frequência do boletim
                  </span>
                  <Select
                    value={String(config?.frequency || "semanal")}
                    onValueChange={(v) => saveConfig({ frequency: v })}
                  >
                    <SelectTrigger data-testid="select-frequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="diaria">Uma vez ao dia</SelectItem>
                      <SelectItem value="duas_vezes_dia">
                        Duas vezes ao dia
                      </SelectItem>
                      <SelectItem value="semanal">Uma vez por semana</SelectItem>
                      <SelectItem value="personalizada">Personalizada</SelectItem>
                    </SelectContent>
                  </Select>
                </label>

                <label className="text-sm space-y-1">
                  <span className="text-muted-foreground">
                    Intervalo mínimo entre mensagens (h)
                  </span>
                  <Input
                    type="number"
                    min={1}
                    max={720}
                    defaultValue={config?.min_interval_hours ?? 12}
                    onBlur={(e) => saveConfig({
                      min_interval_hours: Number(e.target.value),
                    })}
                    data-testid="input-min-interval"
                  />
                </label>
              </div>

              <p className="text-xs text-muted-foreground">
                Toda alteração registra usuário, data, valores anteriores e novos
                na auditoria da unidade.
              </p>
            </Card>
          )}

          <Card className="p-5 space-y-3" data-testid="email-integration">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">
                  Integração de e-mail
                </div>
                <div className="text-xs text-muted-foreground">
                  Serviço usado por boletins, alertas e testes.
                </div>
              </div>
              <Badge
                variant={dashboard?.email?.configured ? "default" : "secondary"}
                className={dashboard?.email?.configured ? "" : "text-amber-700"}
              >
                {dashboard?.email?.configured ? "Configurado" : "Não configurado"}
              </Badge>
            </div>

            {dashboard?.email && (
              <dl className="grid gap-2 sm:grid-cols-2 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground">Provedor</dt>
                  <dd>{dashboard.email.provider}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Remetente</dt>
                  <dd>
                    {dashboard.email.from_name} &lt;{dashboard.email.from_email}&gt;
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">
                    Endereço para respostas
                  </dt>
                  <dd>{dashboard.email.reply_to || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Ambiente</dt>
                  <dd>{dashboard.email.environment}</dd>
                </div>
              </dl>
            )}

            {(dashboard?.email?.hints || []).map((hint) => (
              <p
                key={hint}
                className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700"
              >
                <MailWarning size={14} className="mt-0.5 shrink-0" />
                {hint}
              </p>
            ))}

            {dashboard?.queue && (
              <div className="flex flex-wrap gap-4 border-t pt-3 text-xs text-muted-foreground">
                <span>Fila pendente: {dashboard.queue.pendente}</span>
                <span>Processando: {dashboard.queue.processando}</span>
                <span>Concluídos: {dashboard.queue.concluido}</span>
                <span>Em falha: {dashboard.queue.falha}</span>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-3">
          <div className="flex items-center gap-2">
            <Select
              value={historyOrigin}
              onValueChange={setHistoryOrigin}
            >
              <SelectTrigger
                className="w-48"
                data-testid="history-origin-filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ORIGIN_OPTIONS.map((o) => (
                  <SelectItem key={o.value || "all"} value={o.value || "all"}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="outline"
              onClick={loadHistory}
              data-testid="btn-reload-history"
            >
              <RefreshCw size={14} className="mr-1" /> Recarregar
            </Button>
          </div>

          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs text-muted-foreground">
                <tr>
                  <th className="p-3">Tipo</th>
                  <th className="p-3">Origem</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Motivo</th>
                  <th className="p-3">Risco</th>
                  <th className="p-3">Data</th>
                  <th className="p-3">Rastreio</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="p-6 text-center text-muted-foreground"
                    >
                      Nenhuma comunicação registrada no escopo selecionado.
                    </td>
                  </tr>
                )}
                {history.map((row) => (
                  <tr
                    key={row.id || row.tracking_id}
                    className="border-b last:border-0"
                  >
                    <td className="p-3">
                      <Badge variant="outline">
                        {row.communication_type === "alerta"
                          ? "Alerta" : "Boletim"}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <Badge className={ORIGIN_BADGE[row.origin] || ""}>
                        {row.origin_label || "—"}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <Badge className={STATUS_BADGE[row.status] || ""}>
                        {row.status_label || row.status || "—"}
                      </Badge>
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {row.last_error || row.reason || "—"}
                    </td>
                    <td className="p-3">
                      <Badge className={LEVEL_BADGE[row.current_risk_level] || ""}>
                        {row.risk_from || "—"} → {row.risk_to || "—"}
                      </Badge>
                    </td>
                    <td className="p-3 text-muted-foreground whitespace-nowrap">
                      {fmtDateTime(row.created_at)}
                    </td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">
                      {row.tracking_id || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      <SendNowDialog
        open={sendOpen}
        onOpenChange={setSendOpen}
        clientId={selectedUnit?.client_id}
        unitId={selectedUnit?.id}
        onSent={loadHistory}
      />
      <TestSendDialog
        open={testOpen}
        onOpenChange={setTestOpen}
        clientId={selectedUnit?.client_id}
        unitId={selectedUnit?.id}
        defaultKind={previewTab === "boletim" ? "boletin" : "alerta"}
        onSent={loadHistory}
      />
    </div>
  );
}

function MetricCard({ label, value, hint, tone }) {
  const danger = tone === "danger" && Number(value) > 0;
  return (
    <Card className="p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={`font-heading text-3xl font-bold ${
          danger ? "text-red-600" : ""
        }`}
      >
        {value}
      </div>
      {hint ? (
        <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>
      ) : null}
    </Card>
  );
}


